import type Button from '@mui/material/Button';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ComponentProps, ReactNode, SyntheticEvent } from 'react';

import type { ConstValues } from './helpers.ts';
export interface BaseRow {
  id?: string;
  expanded?: boolean;
  name?: string | null;
  // biome-ignore lint/suspicious/noExplicitAny: any is needed here
  children?: any[] | null;
}

export type RowAction<RowT extends BaseRow> = (row: RowT, e: SyntheticEvent) => void;

export type RowActionState<RowT extends BaseRow> = ((row: RowT) => boolean) | boolean;

export interface RowActionBase<RowT extends BaseRow> {
  label: string | ((row: RowT) => string);
  onClick: RowAction<RowT>;
  allowed?: RowActionState<RowT>;
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | 'default';
  disabled?: RowActionState<RowT>;
  loading?: RowActionState<RowT>;
  hide?: RowActionState<RowT>;
  href?: string | ((row: RowT) => string);
  notAllowedMessage?: string | ((row: RowT) => string);
  'data-test'?: string | ((row: RowT) => string);
  variant?: ComponentProps<typeof Button>['variant'];
}

export interface RowActionButtonBaseProps<RowT extends BaseRow> extends RowActionBase<RowT> {
  notAllowedMessage?: string | ((row: RowT) => string);
}

export interface RowActionProps<RowT extends BaseRow> extends RowActionBase<RowT> {
  topDivider?: boolean;
  error?: boolean;
  danger?: boolean;
  startIcon?: ReactNode;
}

export type Highlighter = (content: string) => ReactNode;

export interface TableColumn<RowT extends BaseRow> {
  title: ReactNode;
  tooltipMessage?: string;
  key: string;
  render?: (row: RowT, highlighter: Highlighter) => ReactNode;
  width?: string;
  calcWidth?: string;
  minWidth?: string;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  multivalue?: boolean;
  multivalueTooltip?: boolean;
  mapBy?: { [id: string]: { name: string } | undefined };
  ellipsis?: boolean;
  ellipsisMaxWidth?: string;
  isDate?: boolean;
  hide?: boolean;
  disabled?: boolean;
  freeze?: boolean;
  isExactDate?: boolean;
  isDateTime?: boolean;
  dataTest?: string;
  headerStyle?: SxProps<Theme>;
  style?: SxProps<Theme>;
  sticky?: StickyPosition;
}

export const STICKY_POSITION = {
  LEFT: 'left',
  RIGHT: 'right',
} as const;

export type StickyPosition = ConstValues<typeof STICKY_POSITION>;

export interface StickyRightCalculations {
  stickyRightOffset?: number;
  isFirstStickyRight: boolean;
}

export type Columns<RowT extends BaseRow> = TableColumn<RowT>[];
export const expandContentRowClass = 'expandContentRow';
export const expandContentCellClass = 'expandContentCell';
